/**
 * InFeedAd — 목록 사이에 끼는 네이티브 광고 한 칸 (수익모델 §3.1).
 *
 * ── 형태: **자리마다 그 자리의 UI 를 그대로 쓴다** (2026-08-27 개정) ──────
 * 처음에는 문서(§3.2)대로 카드 행과 **반대**로 잡았다 — 가로 미디어 + 우측 CTA
 * 버튼. 실제로 얹어 보니 목록 한가운데에 다른 종류의 상자가 끼어드는 모양이라
 * 네이티브가 아니라 배너에 가까웠다. 그래서 뒤집었다:
 *
 *   카드 이미지 자리 → 광고 이미지 · 카드명 자리 → 광고 문구 · CTA 버튼 없음
 *
 * 대신 **`광고` 라벨을 이미지 위에 얹어** 카드가 아니라는 것을 말한다. 형태가
 * 같아진 만큼 이 라벨이 유일한 구분 장치라, 어떤 소재가 와도 덮이지 않게
 * 이미지 **위층**에 그린다.
 *
 * ⚠ 문서 §3.2(형태를 반대로 · CTA 버튼 · 가로 미디어)와 다르다. 문서를 이 결정에
 *   맞춰 고쳐야 한다.
 * ⚠ 딱지에 찍히는 글자는 세 언어 모두 **`ad`** 다 (2026-08-27). 두 글자라
 *   44px 썸네일 모서리에 얹혀도 그림을 덜 가리고, 광고 표시는 이미 만국 공통
 *   기호에 가깝다. 다만 **스크린리더가 읽는 이름은 언어를 따른다**(ad.a11y) —
 *   눈으로 보는 딱지와 달리 소리로는 '광고'라고 들려야 뜻이 선다.
 * ⚠ 라벨은 Astryx Badge 를 쓰지 않는다. Badge 는 알약 모양에 픽토그램이 붙는데,
 *   그건 카드에 붙는 표식(에디션·희귀도)의 생김새다 — 광고 표시가 그 언어를
 *   빌리면 또 하나의 카드 속성처럼 읽힌다.
 * ⚠ 시세 숫자 자리는 **비운다**. 숫자가 오는 순간 카드 행이 된다.
 *
 * ── 지금은 '자리'만 있다 ──────────────────────────────────────────────
 * 광고 SDK 가 아직 안 정해졌다(오픈이슈 OI-09). **개발 빌드에서만** 자리를
 * 그리고 프로덕션에서는 아무것도 내보내지 않는다 — 빈 상자가 목록에 끼어 있는
 * 것이 광고가 없는 것보다 나쁘다.
 * SDK 가 붙으면 이미지·문구만 갈아 끼우면 된다. 자리와 밀도는 adPlacement.ts 가
 * 들고 있어 손댈 필요가 없다.
 */
import { useTranslation } from "react-i18next";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Item } from "@astryxdesign/core/Item";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/Layout";
import styles from "./InFeedAd.module.css";

export interface InFeedAdProps {
  /**
   * 어느 자리에 끼는가 — 그 자리의 UI 를 따른다.
   *   row  : 카드 **행** 목록 (SC-019a · SC-006 목록형 · 홈)
   *   tile : 카드 **타일** 격자 (SC-006 앨범형) — 세로 카드 비율
   *   banner : 목록을 **가로질러 눕는 띠** (SC-002 내 컬렉션)
   */
  variant?: "row" | "tile" | "banner";
  /** 이 화면에서 몇 번째 광고인가 — 개발 빌드 자리표시에만 쓴다 */
  index?: number;
}

/** 광고 이미지 자리 — 카드 그림이 오는 그 상자다. 라벨을 위층에 얹는다. */
function AdMedia({ label, a11y, hint }: { label: string; a11y: string; hint: string }) {
  return (
    <span className={styles.media}>
      <span className={styles.mediaFill}>
        <Text type="supporting" color="secondary">{hint}</Text>
      </span>
      {/* 이미지 **위**다. 소재가 무엇이든 이 표시는 가려지면 안 된다.
          보이는 글자는 `ad`, 읽히는 이름은 그 나라 말이다. */}
      <span className={styles.label} role="img" aria-label={a11y}>
        {label}
      </span>
    </span>
  );
}

export default function InFeedAd({ variant = "row", index }: InFeedAdProps) {
  const { t } = useTranslation("common");

  /* SDK 미연동 — 프로덕션에는 아무것도 내보내지 않는다 (위 주석 참조) */
  if (!import.meta.env.DEV) return null;

  const headline = `${t("ad.placeholder")}${index ? ` ${index}` : ""}`;

  if (variant === "banner") {
    /* 띠 — 컬렉션 격자를 **가로질러 눕는다** (2026-08-28).
       전에는 옆의 컬렉션과 같은 카드 상자였다. 그런데 컬렉션 카드는 이름·금액·
       썸네일이 들어찬 **내 것**인데, 광고는 그 안이 텅 빈 채 같은 상자를 쓰고
       있었다 — 같은 옷을 입었는데 속이 비어서, 네이티브라기보다 **고장 난
       컬렉션 한 칸**처럼 보였다. 상자를 벗기면 남의 자리라는 게 분명해진다.
       ⚠ 격자의 한 칸이 아니라 **전 열을 가로지른다**(styles.banner). 한 칸만
         차지하면 넓은 화면에서 컬렉션 사이에 낀 좁은 조각이 되고, 그때는 다시
         '카드인 척하는 무엇' 이 된다.
       ⚠ 글줄을 두지 않는다. 띠 안에 들어갈 말은 **소재가 들고 온다** — 우리가
         제목·부제 자리를 만들어 두면 소재가 그 틀에 맞을 때만 멀쩡해진다. */
    return (
      <div className={styles.banner} role="complementary" aria-label={t("ad.a11y")}>
        <AdMedia label={t("ad.label")} a11y={t("ad.a11y")} hint={headline} />
      </div>
    );
  }

  if (variant === "tile") {
    /* 앨범형 — 카드 타일과 같은 골격(그림 → 이름 → 부제). 시세 줄은 비운다. */
    return (
      <div className={styles.tile} role="complementary" aria-label={t("ad.a11y")}>
        <VStack gap={1}>
          <AspectRatio ratio={63 / 88}>
            <AdMedia label={t("ad.label")} a11y={t("ad.a11y")} hint={t("ad.placeholder")} />
          </AspectRatio>
          <Text type="body" maxLines={1} wordBreak="break-all">{headline}</Text>
          <Text type="supporting" color="secondary">{t("ad.placeholderHint")}</Text>
        </VStack>
      </div>
    );
  }

  /* 목록형 — 카드 행과 같은 Item 골격. endContent(시세)를 주지 않는다. */
  return (
    <Item
      as="li"
      label={headline}
      description={t("ad.placeholderHint")}
      labelLines={1}
      startContent={<AdMedia label={t("ad.label")} a11y={t("ad.a11y")} hint={t("ad.placeholder")} />}
    />
  );
}
